/**
 * Feature-flag registry — SSOT.
 *
 * Every feature flag in the codebase MUST appear here. The
 * `chiefy/no-stale-feature-flag` ESLint rule reads this module
 * synchronously at lint init and flags consumer call sites whose
 * key:
 *   (a) is NOT registered here — dead reference,
 *   (b) IS registered but `registeredAt` is older than 90 days AND
 *       has no `owner` AND no `ramp` annotation — stale,
 *   (c) starts with `retire-` OR has `deprecated: true` — must be
 *       removed on the retire date.
 *
 * The registry is plain JS (not TS) so the ESLint rule — which runs
 * before any TS build step — can `import` it directly. The companion
 * `registry.d.ts` carries the type shape for TS consumers.
 *
 * Adding a flag:
 *   1. Pick a kebab-case key (e.g. `chat-streaming-v2`).
 *   2. Add an entry: `{ owner, registeredAt, ramp? }`.
 *   3. Implement the consumer (`useFlag` / `isFlagEnabled`).
 *   4. Set a calendar reminder to revisit at +90 days.
 *
 * Retiring a flag:
 *   1. Rename the key to `retire-<original-key>` OR set
 *      `deprecated: true`.
 *   2. The rule will fire on every consumer call site until the
 *      consumers are removed and the registry entry is deleted.
 *
 * Build-time env flags vs runtime feature flags
 * ─────────────────────────────────────────────
 * This registry tracks RUNTIME flags only — the kind that flip per
 * request via URL / localStorage / ramp annotations through
 * `useFlag()` / `isFlagEnabled()`.
 *
 * Build-time gates expressed as `VITE_*` env vars (consumed via
 * `import.meta.env.VITE_…`) live OUTSIDE the registry — they're
 * baked into the bundle at `vite build` time and can't be flipped
 * by the runtime resolver. Document each VITE_ flag inline at the
 * consumer (see `apiUrls.ts::apiOrigin` for `VITE_API_URL`,
 * `analytics.ts` for `VITE_GA_ID`, `useMenteeHint.ts` for
 * `VITE_MENTEE_HINTS_ENABLED`) rather than mixing the two concepts
 * here. The ESLint `no-stale-feature-flag` rule only inspects
 * `useFlag`/`isFlagEnabled` call sites; adding env vars here would
 * create dead entries that look like missing consumer call sites.
 *
 * @typedef {Object} FlagEntry
 * @property {string} owner               GitHub handle / team.
 * @property {string} registeredAt        ISO date (YYYY-MM-DD).
 * @property {boolean=} deprecated        Marks the flag as scheduled for retirement.
 * @property {string=} ramp               Active ramp-up annotation (e.g. "10% prod").
 *
 * @type {Record<string, FlagEntry>}
 */
export const FLAG_REGISTRY = {
    // ── Examples consumed by fixture tests (n1, n8, n9). These also
    //    serve as the canonical entry shapes for real flag additions.
    'valid-fresh-key': {
        owner: 'team-frontend',
        // Anchored well within the 90-day freshness window relative
        // to the rule's NOW reference. Bumped forward whenever the
        // rule's stale-threshold logic is exercised in CI; the value
        // here is the SSOT and is computed by the rule against the
        // current date at lint time.
        registeredAt: '2026-05-01',
    },
    'feature-flag-that-has-active-rampup-annotation': {
        owner: 'team-frontend',
        registeredAt: '2025-01-01',
        ramp: '25% prod (2026-Q2)',
    },
    // ── Stale fixture (p3). Registered, but older than 90 days, no
    //    owner refresh, no ramp annotation — the rule MUST flag it.
    'older-than-90-days-flag': {
        owner: 'team-frontend',
        registeredAt: '2020-01-01',
    },
    // ── Retire-pattern fixture (p4). Name pattern alone is the
    //    decision marker — rule flags every consumer.
    'retire-old-thing': {
        owner: 'team-frontend',
        registeredAt: '2026-05-01',
    },
    // ── Deprecated fixture (p5). Registry mark is the decision —
    //    rule flags every consumer.
    'deprecated-flag': {
        owner: 'team-frontend',
        registeredAt: '2026-05-01',
        deprecated: true,
    },
    // ── Stale-template fixture (p8). Same shape as p3 but used via
    //    a template literal with no interpolation.
    'stale-template-flag': {
        owner: 'team-frontend',
        registeredAt: '2020-01-01',
    },
    // ── Orphan registry entry (p10). Registered, never consumed.
    //    The rule's Program-exit pass flags any entry with no
    //    consumer call site referencing its key.
    'orphan-never-consumed-flag': {
        owner: 'team-frontend',
        registeredAt: '2026-05-01',
    },
    // ── Malformed registeredAt fixture (p12). Registry entry whose
    //    `registeredAt` does not parse as a date — Date.parse returns
    //    NaN and `classifyRegistered` treats that as STALE (drift IS
    //    drift, regardless of whether the date is missing or invalid).
    'malformed-date-flag': {
        owner: 'team-frontend',
        registeredAt: 'not-a-date',
    },
    // ── WS-DISC-3: Global Help Menu (Salesforce-style lower-right
    //    floating pill that opens a 360px slide-over with Glossary
    //    search, OnboardingChecklist, Replay tour, keyboard-shortcut
    //    link and contact support). Mounted in Layout.tsx outside the
    //    rail so it survives route changes. ContextSidebar's
    //    OnboardingChecklist mount is gated to OFF when this flag is ON
    //    (inverse-gate) so the checklist never renders twice during
    //    the staged rollout.
    'help-menu-v1': {
        owner: 'team-frontend',
        registeredAt: '2026-06-02',
        ramp: 'progressive rollout',
    },
    // ── WS-M-3: Concern state machine + RaiseConcernModal + ConcernTimeline.
    //    Atlassian-style 5-state machine (open → in_review → resolved |
    //    withdrawn | escalated) gated while the backend
    //    `disputes.state + disputes.version` migration is in flight.
    //    Frontend ships first — the BE migration unblocks ramp.
    'concern-state-machine': {
        owner: 'team-frontend',
        registeredAt: '2026-06-02',
        ramp: 'pending backend migration',
    },
    // ── WS-FTUE-3 PairingCard mock-stage gate. The backend
    //    `/api/mentorship/pairing/status/` endpoint shipped (BE commit
    //    8bf52b52) and the FE now drives off it by default. This flag
    //    is RETAINED so dev / staging environments can still cycle the
    //    mock through the four stages without touching the BE — the
    //    per-stage value lives in `chiefy.pairing.mock-stage` (1..4).
    //    Ramp annotation no longer carries `100%` so prod resolves the
    //    flag OFF and uses the real fetcher; per-tester opt-in still
    //    works through the URL / localStorage override path. Retire
    //    this entry once the QA stage-cycling tooling is migrated to
    //    a BE-side fixture endpoint.
    'pairing-mock-stage': {
        owner: 'team-frontend',
        registeredAt: '2026-06-02',
        ramp: 'dev/staging only (per-tester override; prod uses real endpoint)',
    },
    // ── §9 IA Collapse: Primary Rail adoption gate.
    //    When ON, Sidebar renders SidebarPrimaryAdapter (5-item
    //    PrimaryRail) instead of the legacy NavGroups 6-tab list.
    //    Key mapping: home→home, mentorship→mentorship,
    //    settings→settings, messages→messages (hidden legacy tab now
    //    surfaced); assistant/education/networking/profile have no
    //    rail equivalent and fall back to 'home'.
    //    New rail item `insights` (no legacy equivalent) is accessible
    //    only once this flag is ON.
    //    DEFAULT OFF in all environments — legacy Sidebar is untouched
    //    until an explicit URL/localStorage override or a 100% ramp
    //    annotation is added.
    'primary-rail': {
        owner: 'team-frontend',
        registeredAt: '2026-06-03',
        ramp: 'pending §9 IA collapse rollout',
    },
};

/**
 * Names of the consumer hook(s) the rule visits. Exported so the
 * rule (and any future debug tooling) can stay in sync with the
 * registry without a second SSOT.
 *
 * @type {readonly string[]}
 */
export const CONSUMER_HOOK_NAMES = Object.freeze(['useFlag', 'isFlagEnabled']);

/**
 * Stale threshold in days. Mirrors the rule's docstring contract.
 * Exported so the rule can derive its window from the SSOT.
 */
export const STALE_THRESHOLD_DAYS = 90;
